import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Star, Users, CheckCircle, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { mockData, iconMap } from '../mock';

const GetStartedPage = () => {
  const { serviceSlug } = useParams();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [consent, setConsent] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '' });

  // Find the service across all categories
  let service = null;
  let category = null;
  for (const cat of mockData.serviceCategories) {
    const found = cat.services.find(s => s.slug === serviceSlug);
    if (found) {
      service = found;
      category = cat;
      break;
    }
  }

  const relatedServices = React.useMemo(() =>
    category ? category.services.filter(s => s.slug !== serviceSlug) : [],
    [category, serviceSlug]
  );

  if (!service) {
    return (
      <div className="getstarted-not-found" data-testid="service-not-found">
        <h2>Service not found</h2>
        <Link to="/">
          <Button className="btn-primary"><ArrowLeft size={18} /> Back to Home</Button>
        </Link>
      </div>
    );
  }

  const IconComponent = iconMap[service.icon] || Star;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          email: form.email,
          service: service.name,
          category: category.title,
        }),
      });
    } catch (err) {
      console.error('Form submission failed:', err);
    }
    setSubmitted(true);
  };

  return (
    <div className="getstarted-page" data-testid="get-started-page">
      <div className="getstarted-container">
        {/* Breadcrumb */}
        <div className="getstarted-breadcrumb">
          <Link to="/" className="breadcrumb-link">Home</Link>
          <span className="breadcrumb-sep">/</span>
          <Link to="/services" className="breadcrumb-link">{category.title}</Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">{service.name}</span>
        </div>

        {/* Category Details Section */}
        {category.details && !category.details.sections && (
          <div className="category-details" data-testid="category-details">
            <p className="category-tagline">{category.details.tagline}</p>
            <p className="category-description">{category.details.description}</p>

            <h3 className="category-highlights-heading">{category.details.heading}</h3>
            <ul className="category-highlights">
              {category.details.highlights.map((item, idx) => (
                <li key={idx} className="category-highlight-item">{item}</li>
              ))}
            </ul>

            {category.details.subServices && (
              <>
                <h4 className="category-subservices-heading">Sub-Services</h4>
                <div className="category-subservices">
                  {category.details.subServices.map((sub, idx) => (
                    <span key={idx} className="category-subservice-tag">{sub}</span>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Multi-Section Details (PMS & AIFs) */}
        {category.details && category.details.sections && (
          <div className="category-details-multi" data-testid="category-details-multi">
            {category.details.sections.map((section, sIdx) => (
              <div key={sIdx} className="detail-section">
                <h2 className="detail-section-title">{section.title}</h2>
                <p className="category-tagline">{section.tagline}</p>
                <p className="category-description">{section.description}</p>

                {section.minimumInvestment && (
                  <div className="detail-min-investment">
                    <span className="min-invest-label">SEBI Minimum Investment:</span>
                    <span className="min-invest-value">{section.minimumInvestment}</span>
                  </div>
                )}

                {section.pmsTypes && (
                  <div className="detail-types-grid">
                    {section.pmsTypes.map((t, i) => (
                      <div key={i} className="detail-type-card">
                        <h4 className="detail-type-name">{t.name}</h4>
                        <p className="detail-type-desc">{t.desc}</p>
                      </div>
                    ))}
                  </div>
                )}

                {section.aifCategories && (
                  <div className="detail-types-grid">
                    {section.aifCategories.map((cat, i) => (
                      <div key={i} className="detail-type-card">
                        <h4 className="detail-type-name">{cat.name}</h4>
                        <p className="detail-type-focus">{cat.focus}</p>
                        <ul className="detail-type-items">
                          {cat.items.map((item, j) => (
                            <li key={j}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {section.stats && (
                  <div className="detail-stats-bar">
                    {section.stats.map((stat, i) => (
                      <div key={i} className="detail-stat">
                        <span className="detail-stat-value">{stat.value}</span>
                        <span className="detail-stat-label">{stat.label}</span>
                      </div>
                    ))}
                  </div>
                )}

                <h3 className="category-highlights-heading">{section.heading}</h3>
                <ul className="category-highlights">
                  {section.highlights.map((item, idx) => (
                    <li key={idx} className="category-highlight-item">{item}</li>
                  ))}
                </ul>

                {section.subServices && (
                  <>
                    <h4 className="category-subservices-heading">Sub-Services</h4>
                    <div className="category-subservices">
                      {section.subServices.map((sub, idx) => (
                        <span key={idx} className="category-subservice-tag">{sub}</span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}

            {category.details.comparisonTable && (
              <div className="detail-comparison">
                <h3 className="comparison-title">PMS vs AIF — Quick Comparison</h3>
                <div className="comparison-table-wrapper">
                  <table className="comparison-table">
                    <thead>
                      <tr>
                        {category.details.comparisonTable.headers.map((h, i) => (
                          <th key={i}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {category.details.comparisonTable.rows.map((row, i) => (
                        <tr key={i}>
                          {row.map((cell, j) => (
                            <td key={j}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {category.details.riskDisclosure && (
              <div className="detail-risk-disclosure">
                <p>{category.details.riskDisclosure}</p>
              </div>
            )}
          </div>
        )}

        <div className="getstarted-grid">
          {/* Left - Service Info */}
          <div className="getstarted-info">
            <div className="getstarted-icon-wrapper">
              <IconComponent size={40} />
            </div>
            <h1 className="getstarted-title" data-testid="getstarted-title">{service.name}</h1>
            <p className="getstarted-category">{category.title}</p>

            <div className="getstarted-meta">
              <span className="getstarted-rating">
                <Star size={16} fill="#00A86B" stroke="#00A86B" /> {service.rating}
              </span>
              <span className="getstarted-users">
                <Users size={16} /> {service.users} users assisted
              </span>
            </div>

            <div className="getstarted-steps">
              <h3 className="getstarted-steps-title">What you'll get</h3>
              <div className="getstarted-step-item">
                <CheckCircle size={18} className="step-check" />
                <span>Personalized consultation with our expert</span>
              </div>
              <div className="getstarted-step-item">
                <CheckCircle size={18} className="step-check" />
                <span>Tailored strategy based on your goals</span>
              </div>
              <div className="getstarted-step-item">
                <CheckCircle size={18} className="step-check" />
                <span>Actionable plan of action within 24 hours</span>
              </div>
              <div className="getstarted-step-item">
                <CheckCircle size={18} className="step-check" />
                <span>Follow-up support and Q&A</span>
              </div>
            </div>

            <div className="getstarted-contact-info">
              <p><Phone size={16} /> {mockData.contact.phone}</p>
              <p><Mail size={16} /> {mockData.contact.email}</p>
            </div>
          </div>

          {/* Right - Form */}
          <div className="getstarted-form-wrapper">
            {!submitted ? (
              <>
                <h2 className="getstarted-form-title">Get Expert Assistance</h2>
                <p className="getstarted-form-subtitle">Share your details and our team will reach out within 24 hours</p>
                <form className="getstarted-form" onSubmit={handleSubmit} data-testid="getstarted-form">
                  <div className="gs-form-group">
                    <label className="gs-label">Name *</label>
                    <input
                      type="text"
                      className="gs-input"
                      data-testid="gs-name-input"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  <div className="gs-form-group">
                    <label className="gs-label">Mobile *</label>
                    <input
                      type="tel"
                      className="gs-input"
                      data-testid="gs-phone-input"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+91 XXXXX XXXXX"
                      required
                    />
                  </div>
                  <div className="gs-form-group">
                    <label className="gs-label">Email *</label>
                    <input
                      type="email"
                      className="gs-input"
                      data-testid="gs-email-input"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                  <div className="gs-consent-group" data-testid="gs-consent-group">
                    <label className="gs-consent-label">
                      <input
                        type="checkbox"
                        className="gs-consent-checkbox"
                        data-testid="gs-consent-checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        required
                      />
                      <span>I consent to receiving WhatsApp messages and emails from APK FINTECH regarding my query.</span>
                    </label>
                  </div>
                  <Button type="submit" className="btn-primary gs-submit" data-testid="gs-submit-btn" disabled={!consent}>
                    Submit Request <ArrowRight size={18} />
                  </Button>
                </form>
              </>
            ) : (
              <div className="getstarted-success" data-testid="gs-success">
                <CheckCircle size={56} className="success-icon" />
                <h2 className="success-title">Request Submitted!</h2>
                <p className="success-text">
                  Thank you, {form.name}! Our team will reach out to you within 24 hours regarding <strong>{service.name}</strong>.
                </p>
                <Button className="btn-primary" onClick={() => navigate('/')}>
                  <ArrowLeft size={18} /> Back to Home
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Other services in same category */}
        <div className="getstarted-related">
          <h3 className="related-title">Other services in {category.title}</h3>
          <div className="related-grid">
            {relatedServices.map((s, idx) => {
                const RelIcon = iconMap[s.icon] || Star;
                return (
                  <div
                    key={idx}
                    className="catalog-card"
                    onClick={() => { navigate(`/get-started/${s.slug}`); setSubmitted(false); setConsent(false); setForm({ name: '', phone: '', email: '' }); }}
                  >
                    <div className="catalog-card-icon"><RelIcon size={28} /></div>
                    <p className="catalog-card-name">{s.name}</p>
                    <div className="catalog-card-meta">
                      <span className="catalog-card-rating">{s.rating} <Star size={12} fill="#00A86B" stroke="#00A86B" /></span>
                      <span className="catalog-card-users">{s.users} <Users size={12} /></span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GetStartedPage;
